/* anki-apkg.js — build a real Anki .apkg deck entirely in the browser.
 *
 * An .apkg is a ZIP of `collection.anki2` (a SQLite v11 database) plus a
 * `media` JSON map. This module builds the SQLite with sql.js and zips it
 * with fflate. Cards use Anki's built-in TTS template tag, so generated
 * decks speak each word with NO bundled audio files.
 *
 * Modern Anki desktop, AnkiDroid and AnkiMobile all import schema v11.
 *
 * Depends on: /js/vendor/sql-wasm/sql-wasm.js (+ .wasm), /js/vendor/fflate.min.js
 * Exposes window.AnkiApkg.
 */
(function (root, factory) {
  'use strict';
  var A = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = A;
  if (typeof window !== 'undefined') window.AnkiApkg = A;
  else if (root) root.AnkiApkg = A;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var SEP = '\u001F';                          // Anki field separator (0x1f)
  var SQL = null, _initPromise = null;

  /* ── compact synchronous SHA-1 (for csum + stable guid) ───────── */
  function sha1(str) {
    function rotl(n, s) { return (n << s) | (n >>> (32 - s)); }
    var bytes = [], i, c;
    for (i = 0; i < str.length; i++) {
      c = str.charCodeAt(i);
      if (c < 128) bytes.push(c);
      else if (c < 2048) bytes.push(192 | (c >> 6), 128 | (c & 63));
      else bytes.push(224 | (c >> 12), 128 | ((c >> 6) & 63), 128 | (c & 63));
    }
    var ml = bytes.length * 8;
    bytes.push(0x80);
    while (bytes.length % 64 !== 56) bytes.push(0);
    for (i = 7; i >= 0; i--) bytes.push(Math.floor(ml / Math.pow(2, i * 8)) & 255);
    var h0 = 0x67452301, h1 = 0xEFCDAB89, h2 = 0x98BADCFE, h3 = 0x10325476, h4 = 0xC3D2E1F0;
    for (var j = 0; j < bytes.length; j += 64) {
      var w = [], a, b, d, e, f, k, t;
      for (i = 0; i < 16; i++)
        w[i] = (bytes[j + i * 4] << 24) | (bytes[j + i * 4 + 1] << 16) |
               (bytes[j + i * 4 + 2] << 8) | bytes[j + i * 4 + 3];
      for (i = 16; i < 80; i++) w[i] = rotl(w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16], 1);
      a = h0; b = h1; var cc = h2; d = h3; e = h4;
      for (i = 0; i < 80; i++) {
        if (i < 20) { f = (b & cc) | ((~b) & d); k = 0x5A827999; }
        else if (i < 40) { f = b ^ cc ^ d; k = 0x6ED9EBA1; }
        else if (i < 60) { f = (b & cc) | (b & d) | (cc & d); k = 0x8F1BBCDC; }
        else { f = b ^ cc ^ d; k = 0xCA62C1D6; }
        t = (rotl(a, 5) + f + e + k + w[i]) | 0;
        e = d; d = cc; cc = rotl(b, 30); b = a; a = t;
      }
      h0 = (h0 + a) | 0; h1 = (h1 + b) | 0; h2 = (h2 + cc) | 0;
      h3 = (h3 + d) | 0; h4 = (h4 + e) | 0;
    }
    function hex(n) { var s = (n >>> 0).toString(16); return '00000000'.slice(s.length) + s; }
    return hex(h0) + hex(h1) + hex(h2) + hex(h3) + hex(h4);
  }

  function stripHtml(s) { return String(s || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' '); }

  /* ── dynamic dependency loading ───────────────────────────────── */
  function loadScript(src) {
    return new Promise(function (res, rej) {
      var s = document.createElement('script');
      s.src = src; s.onload = res; s.onerror = function () { rej(new Error('load ' + src)); };
      document.head.appendChild(s);
    });
  }

  function init(opts) {
    opts = opts || {};
    if (_initPromise) return _initPromise;
    var wasmDir = opts.wasmDir || '/js/vendor/sql-wasm/';
    _initPromise = Promise.resolve()
      .then(function () {
        if (typeof window !== 'undefined' && !window.fflate)
          return loadScript('/js/vendor/fflate.min.js');
      })
      .then(function () {
        if (typeof window !== 'undefined' && typeof window.initSqlJs !== 'function')
          return loadScript(wasmDir + 'sql-wasm.js');
      })
      .then(function () {
        return window.initSqlJs({ locateFile: function (f) { return wasmDir + f; } });
      })
      .then(function (sql) { SQL = sql; return true; })
      .catch(function (e) { _initPromise = null; throw e; });
    return _initPromise;
  }

  /* ── card model templates ─────────────────────────────────────── */
  var CSS = '.card{font-family:arial;font-size:20px;text-align:center;color:#111;background:#fff;}' +
            '.ipa{color:#3a7bd5;font-size:16px;}.ex{color:#666;font-size:15px;font-style:italic;}';
  function modelDef(kind, mid, did) {
    var nowSec = Math.floor(Date.now() / 1000);
    var qfmt = '{{Front}}';
    if (kind === 'basic-tts') qfmt += '<br>[anki:tts lang=en_US]{{Front}}[/anki:tts]';
    return {
      id: mid, name: kind === 'basic-tts' ? 'Timbrica Basic+TTS' : 'Timbrica Basic',
      type: 0, mod: nowSec, usn: -1, sortf: 0, did: did, latexPre: '', latexPost: '',
      flds: [
        { name: 'Front', ord: 0, sticky: false, rtl: false, font: 'Arial', size: 20 },
        { name: 'Back', ord: 1, sticky: false, rtl: false, font: 'Arial', size: 20 }
      ],
      tmpls: [{ name: 'Card 1', ord: 0, qfmt: qfmt,
        afmt: '{{FrontSide}}<hr id=answer>{{Back}}', bqfmt: '', bafmt: '', did: null }],
      css: CSS, req: [[0, 'any', [0]]]
    };
  }

  /* ── Builder ──────────────────────────────────────────────────── */
  function Builder(spec) {
    this.deckName = (spec && spec.deckName) || 'Timbrica deck';
    this.modelKind = (spec && spec.model) || 'basic-tts';
    this._notes = [];
  }
  Builder.prototype.addNote = function (fields, tags, sched) {
    this._notes.push({ fields: fields.slice(), tags: tags || '', sched: sched || null });
    return this;
  };
  Builder.prototype.note = function (obj) {
    return this.addNote([obj.front || '', obj.back || ''], obj.tags, obj.sched);
  };
  Builder.prototype.count = function () { return this._notes.length; };

  Builder.prototype.buildBytes = function () {
    if (!SQL) throw new Error('AnkiApkg.init() not called');
    var db = new SQL.Database();
    var nowMs = Date.now(), nowSec = Math.floor(nowMs / 1000);
    var crt = Math.floor(new Date().setHours(0, 0, 0, 0) / 1000);
    var mid = nowMs, did = nowMs + 1;

    db.run(
      'CREATE TABLE col(id integer primary key,crt integer not null,mod integer not null,' +
      'scm integer not null,ver integer not null,dty integer not null,usn integer not null,' +
      'ls integer not null,conf text not null,models text not null,decks text not null,' +
      'dconf text not null,tags text not null);' +
      'CREATE TABLE notes(id integer primary key,guid text not null,mid integer not null,' +
      'mod integer not null,usn integer not null,tags text not null,flds text not null,' +
      'sfld integer not null,csum integer not null,flags integer not null,data text not null);' +
      'CREATE TABLE cards(id integer primary key,nid integer not null,did integer not null,' +
      'ord integer not null,mod integer not null,usn integer not null,type integer not null,' +
      'queue integer not null,due integer not null,ivl integer not null,factor integer not null,' +
      'reps integer not null,lapses integer not null,left integer not null,odue integer not null,' +
      'odid integer not null,flags integer not null,data text not null);' +
      'CREATE TABLE revlog(id integer primary key,cid integer not null,usn integer not null,' +
      'ease integer not null,ivl integer not null,lastIvl integer not null,factor integer not null,' +
      'time integer not null,type integer not null);' +
      'CREATE TABLE graves(usn integer not null,oid integer not null,type integer not null);' +
      'CREATE INDEX ix_notes_usn on notes(usn);CREATE INDEX ix_cards_usn on cards(usn);' +
      'CREATE INDEX ix_revlog_usn on revlog(usn);CREATE INDEX ix_cards_nid on cards(nid);' +
      'CREATE INDEX ix_cards_sched on cards(did,queue,due);' +
      'CREATE INDEX ix_revlog_cid on revlog(cid);CREATE INDEX ix_notes_csum on notes(csum);'
    );

    var model = modelDef(this.modelKind, mid, did);
    var models = {}; models[mid] = model;
    var defDeck = function (id, name) {
      return { id: id, name: name, mod: 0, usn: 0, collapsed: false, desc: '', dyn: 0,
        conf: 1, extendNew: 0, extendRev: 0, newToday: [0, 0], revToday: [0, 0],
        lrnToday: [0, 0], timeToday: [0, 0] };
    };
    var decks = { 1: defDeck(1, 'Default') };
    decks[did] = defDeck(did, this.deckName);
    var dconf = { 1: { id: 1, name: 'Default', mod: 0, usn: 0, maxTaken: 60, autoplay: true,
      timer: 0, replayq: true,
      new: { bury: true, delays: [1, 10], initialFactor: 2500, ints: [1, 4, 7], order: 1, perDay: 20, separate: true },
      rev: { bury: true, ease4: 1.3, fuzz: 0.05, ivlFct: 1, maxIvl: 36500, minSpace: 1, perDay: 200 },
      lapse: { delays: [10], leechAction: 0, leechFails: 8, minInt: 1, mult: 0 } } };
    var conf = { nextPos: this._notes.length + 1, estTimes: true, activeDecks: [1],
      sortType: 'noteFld', timeLim: 0, sortBackwards: false, addToCur: true, curDeck: did,
      newSpread: 0, dueCounts: true, curModel: String(mid), collapseTime: 1200 };

    db.run('INSERT INTO col VALUES (1,?,?,?,11,0,0,0,?,?,?,?,?)',
      [crt, nowMs, nowMs, JSON.stringify(conf), JSON.stringify(models),
       JSON.stringify(decks), JSON.stringify(dconf), '{}']);

    for (var i = 0; i < this._notes.length; i++) {
      var note = this._notes[i];
      var nid = nowMs + i, cid = nowMs + 100000 + i;
      var flds = note.fields.join(SEP);
      var sfld = stripHtml(note.fields[0] || '');
      var guid = sha1(flds).slice(0, 10);
      var csum = parseInt(sha1(sfld).slice(0, 8), 16);
      var tags = note.tags ? ' ' + String(note.tags).trim() + ' ' : '';
      db.run('INSERT INTO notes VALUES (?,?,?,?,-1,?,?,?,?,0,?)',
        [nid, guid, mid, nowSec, tags, flds, sfld, csum, '']);
      var s = note.sched || null;
      var c = { type: 0, queue: 0, due: i + 1, ivl: 0, factor: 0, reps: 0, lapses: 0 };
      if (s && typeof s === 'object') {
        c.type = s.type || 0; c.queue = s.queue || 0; c.due = s.due || (i + 1);
        c.ivl = s.ivl || 0; c.factor = s.factor || 0; c.reps = s.reps || 0; c.lapses = s.lapses || 0;
      }
      db.run('INSERT INTO cards VALUES (?,?,?,0,?,-1,?,?,?,?,?,?,?,0,0,0,0,?)',
        [cid, nid, did, nowSec, c.type, c.queue, c.due, c.ivl, c.factor, c.reps, c.lapses, '']);
    }

    var anki2 = db.export();
    db.close();
    var zip = window.fflate.zipSync({
      'collection.anki2': anki2,
      'media': window.fflate.strToU8('{}')
    }, { level: 6 });
    return zip;
  };

  Builder.prototype.build = function () {
    var self = this;
    return Promise.resolve(SQL ? null : init()).then(function () {
      var bytes = self.buildBytes();
      return new Blob([bytes], { type: 'application/zip' });
    });
  };

  function download(blob, name) {
    name = name || 'deck.apkg';
    if (window.__downloadFile) { window.__downloadFile(blob, name); return; }
    var a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = name;
    document.body.appendChild(a); a.click();
    setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
  }

  return {
    init: init,
    create: function (spec) { return new Builder(spec); },
    download: download,
    _sha1: sha1
  };
});
